import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import classNames from "classnames";

interface ItineraryMapProps {
    tourOperator: any
    itineraryDays: any
    countryCodes?: string[]
    version?: string
}

export const ItineraryMap : React.FC<ItineraryMapProps> = ({
                                                               tourOperator,
                                                               itineraryDays,
                                                               countryCodes,
    version = "pdf"
                                                           }) => {




    const svgRef = useRef<SVGSVGElement | null>(null);

    // Función para detectar colisiones entre rectángulos con padding
    const detectCollision = (rect1: any, rect2: any, padding: number = 10) => {
        return !(rect1.x + rect1.width + padding < rect2.x ||
            rect2.x + rect2.width + padding < rect1.x ||
            rect1.y + rect1.height + padding < rect2.y ||
            rect2.y + rect2.height + padding < rect1.y);
    };

    // Función para detectar colisión entre rectángulo y círculo con padding
    const detectCircleRectCollision = (rect: any, circle: any, padding: number = 15) => {
        const circleRadius = 8 + padding; // Radio del círculo + padding
        const rectCenterX = rect.x + rect.width / 2;
        const rectCenterY = rect.y + rect.height / 2;

        // Distancia entre centros
        const dx = Math.abs(circle.x - rectCenterX);
        const dy = Math.abs(circle.y - rectCenterY);

        // Si está muy lejos, no hay colisión
        if (dx > (rect.width / 2 + circleRadius) || dy > (rect.height / 2 + circleRadius)) {
            return false;
        }

        // Si está muy cerca del centro, hay colisión
        if (dx <= rect.width / 2 || dy <= rect.height / 2) {
            return true;
        }

        // Verificar colisión en las esquinas
        const cornerDx = dx - rect.width / 2;
        const cornerDy = dy - rect.height / 2;
        return (cornerDx * cornerDx + cornerDy * cornerDy <= circleRadius * circleRadius);
    };

    // Función para mantener rectángulos dentro de los límites del SVG
    const constrainToBounds = (label: any, width: number, height: number, margin: number = 10) => {
        // Limitar posición X
        if (label.x < margin) {
            label.x = margin;
        } else if (label.x + label.width > width - margin) {
            label.x = width - label.width - margin;
        }

        // Limitar posición Y
        if (label.y < margin) {
            label.y = margin;
        } else if (label.y + label.height > height - margin) {
            label.y = height - label.height - margin;
        }

        return label;
    };

    // Función para ajustar posiciones evitando solapamientos
    const adjustLabelPositions = (labels: any[], circles: any[], svgWidth: number, svgHeight: number) => {
        const adjustedLabels = labels.map(label => ({ ...label }));
        const maxIterations = 100;
        const repulsionForce = 3;
        const padding = 10;
        const margin = 10;

        for (let iteration = 0; iteration < maxIterations; iteration++) {
            let hasCollisions = false;

            // Verificar colisiones entre etiquetas
            for (let i = 0; i < adjustedLabels.length; i++) {
                for (let j = i + 1; j < adjustedLabels.length; j++) {
                    const label1 = adjustedLabels[i];
                    const label2 = adjustedLabels[j];

                    if (detectCollision(label1, label2, padding)) {
                        hasCollisions = true;

                        // Calcular vector de separación entre centros
                        const dx = (label2.x + label2.width/2) - (label1.x + label1.width/2);
                        const dy = (label2.y + label2.height/2) - (label1.y + label1.height/2);
                        const distance = Math.sqrt(dx * dx + dy * dy) || 1;

                        // Normalizar y aplicar fuerza de repulsión
                        const nx = dx / distance;
                        const ny = dy / distance;

                        label1.x -= nx * repulsionForce;
                        label1.y -= ny * repulsionForce;
                        label2.x += nx * repulsionForce;
                        label2.y += ny * repulsionForce;
                    }
                }

                // Verificar colisiones entre etiquetas y círculos
                for (let k = 0; k < circles.length; k++) {
                    const label = adjustedLabels[i];
                    const circle = circles[k];

                    if (detectCircleRectCollision(label, circle, padding)) {
                        hasCollisions = true;

                        // Calcular vector de separación desde círculo hacia etiqueta
                        const dx = (label.x + label.width/2) - circle.x;
                        const dy = (label.y + label.height/2) - circle.y;
                        const distance = Math.sqrt(dx * dx + dy * dy) || 1;

                        // Normalizar y aplicar fuerza de repulsión
                        const nx = dx / distance;
                        const ny = dy / distance;

                        // Mover etiqueta lejos del círculo
                        label.x += nx * repulsionForce;
                        label.y += ny * repulsionForce;
                    }
                }

                // Aplicar límites del SVG después de cada ajuste
                adjustedLabels[i] = constrainToBounds(adjustedLabels[i], svgWidth, svgHeight, margin);
            }

            if (!hasCollisions) break;
        }

        // Aplicar límites finales a todas las etiquetas
        return adjustedLabels.map(label => constrainToBounds(label, svgWidth, svgHeight, margin));
    };

    useEffect(() => {
        if (!svgRef.current) return;

        // Clear any existing content
        d3.select(svgRef.current).selectAll("*").remove();

        // Set up dimensions
        const width = svgRef.current.clientWidth;
        const height = svgRef.current.clientHeight;

        // Create SVG with desired dimensions
        const svg = d3.select(svgRef.current)
            .attr("width", width)
            .attr("height", height)
            .attr("viewBox", [0, 0, width, height])
            .style("background", "transparent");



        //flatten itineraryDays to get all locations and remove duplicates
        const allLocations = itineraryDays.flatMap((day: any) => {
            return day.locations?.map((location: any) => { return {
                name: location.name,
                coordinates: location.coordinates ? [Number(location.coordinates.longitude), Number(location.coordinates.latitude)] : [0, 0]
            }}) || [];
        });

        // Remove duplicates based on coordinates
        const locations = allLocations.filter((location, index, self) =>
                index === self.findIndex(l =>
                    l.coordinates[0] === location.coordinates[0] &&
                    l.coordinates[1] === location.coordinates[1]
                )
        );

        // Fetch country map data - using Spain as an example
        // You can replace this URL with any country you need
        fetch(`https://storage.googleapis.com/tripsapp-prod-cdn/geojson/countries/${countryCodes[0]}.geo.json`)
            .then(response => response.json())
            .then(geoData => {
                // Create a projection for the map
                const projection = d3.geoMercator()
                    .fitSize([width, height], geoData);

                // Create a path generator
                const path = d3.geoPath().projection(projection);

                // Append the country paths
                svg.selectAll("path")
                    .data(geoData.features)
                    .join("path")
                    .attr("d", path)
                    .attr("fill", version === "web" ? tourOperator.secondaryColor : "#ffffff")
                    .attr("stroke",  version === "web"?  tourOperator.secondaryColor :  "#cccccc")
                    .attr("stroke-width", "1pt");

                // Crear grupos en el orden correcto de renderizado
                const linesGroup = svg.append("g").attr("class", "lines-group");
                const labelsGroup = svg.append("g").attr("class", "labels-group");
                const circlesGroup = svg.append("g").attr("class", "circles-group");

                // Preparar datos de círculos para detección de colisiones
                const circleData = locations.map(location => {
                    const projected = projection(location.coordinates);
                    return {
                        x: projected ? projected[0] : 0,
                        y: projected ? projected[1] : 0,
                        radius: 8
                    };
                });

                // Preparar datos de etiquetas con posiciones iniciales
                const labelData = locations.map(location => {
                    const projected = projection(location.coordinates);
                    const textWidth = location.name.length * 13;
                    const textHeight = 25;

                    return {
                        name: location.name,
                        coordinates: location.coordinates,
                        originalX: projected ? projected[0] : 0,
                        originalY: projected ? projected[1] - 30 : 0,
                        x: projected ? projected[0] - textWidth / 2 : 0,
                        y: projected ? projected[1] - 30 - textHeight / 2 : 0,
                        width: textWidth,
                        height: textHeight,
                        projected: projected
                    };
                });

                // Ajustar posiciones para evitar solapamientos con círculos y otras etiquetas
                const adjustedLabels = adjustLabelPositions(labelData, circleData, width, height);

                // Dibujar líneas curvas PRIMERO (detrás de todo)
                for (let i = 0; i < locations.length - 1; i++) {
                    const startLoc = locations[i];
                    const endLoc = locations[i + 1];

                    const startPoint = projection(startLoc.coordinates);
                    const endPoint = projection(endLoc.coordinates);

                    if (startPoint && endPoint) {
                        // Calculate distance between points to determine arc height
                        const dx = endPoint[0] - startPoint[0];
                        const dy = endPoint[1] - startPoint[1];
                        const distance = Math.sqrt(dx * dx + dy * dy);

                        // Make arc height more pronounced (30% of distance)
                        const arcHeight = distance * 0.3;

                        // Calculate control point for more pronounced curve
                        const midX = (startPoint[0] + endPoint[0]) / 2;
                        const midY = (startPoint[1] + endPoint[1]) / 2;

                        // Perpendicular vector for control point
                        const nx = -dy / distance;
                        const ny = dx / distance;

                        // Control point position (more pronounced curve)
                        const controlX = midX + nx * arcHeight;
                        const controlY = midY + ny * arcHeight;

                        // Create the path with a quadratic curve
                        linesGroup.append("path")
                            .attr("d", `M${startPoint[0]},${startPoint[1]} Q${controlX},${controlY} ${endPoint[0]},${endPoint[1]}`)
                            .attr("fill", "none")
                            .attr("stroke", version === "web" ? tourOperator.primaryColor  : tourOperator.secondaryColor || tourOperator.primaryColor || "#000000")
                            .attr("stroke-width", "2px")
                            .attr("stroke-dasharray", "5,2"); // Add dashed pattern
                    }
                }

                // Dibujar etiquetas ANTES que los círculos (para que queden debajo)
                const labelGroups = labelsGroup.selectAll("g.location-label")
                    .data(adjustedLabels)
                    .join("g")
                    .attr("class", "location-label");

                // Agregar líneas conectoras desde el punto original hasta la etiqueta ajustada
                /*
                labelGroups.append("line")
                    .attr("x1", d => d.originalX)
                    .attr("y1", d => d.originalY + 30)
                    .attr("x2", d => d.x + d.width / 2)
                    .attr("y2", d => d.y + d.height / 2)
                    .attr("stroke", tourOperator.primaryColor || "#000000")
                    .attr("stroke-width", "1px")
                    .attr("stroke-dasharray", "2,2")
                    .attr("opacity", 0.6);

                 */

                // Add white background boxes with blue border for the text
                labelGroups.append("rect")
                    .attr("x", d => d.x)
                    .attr("y", d => d.y)
                    .attr("width", d => d.width)
                    .attr("height", d => d.height)
                    .attr("fill", "white")
                    .attr("stroke", version === "web" ? '#1A1A1A' : tourOperator.secondaryColor || tourOperator.primaryColor || "#000000")
                    .attr("stroke-width", version === "web" ? 0 : 1)
                    .attr("rx", 12) // Rounded corners
                    .attr("ry", 12);

                // Add text labels on top of the background boxes
                labelGroups.append("text")
                    .attr("x", d => d.x + d.width / 2)
                    .attr("y", d => d.y + d.height / 2 + 4) // +4 para centrar verticalmente
                    .attr("text-anchor", "middle")
                    .attr("font-size", version === "web" ? '11pt' :"12pt")
                    .attr("fill", version === "web" ? '#1A1A1A' : tourOperator.secondaryColor || tourOperator.primaryColor || "#000000")
                    .text(d => d.name);

                // Dibujar círculos AL FINAL (encima de las etiquetas)
                circlesGroup.selectAll("circle")
                    .data(locations.map(loc => loc.coordinates))
                    .join("circle")
                    .attr("cx", d => projection(d)[0])
                    .attr("cy", d => projection(d)[1])
                    .attr("r", "8px")
                    .attr("fill", version === "web" ? tourOperator.primaryColor  : tourOperator.secondaryColor || tourOperator.primaryColor || "#000000");
            })
            .catch(error => console.error("Error loading the map data:", error));
    }, [itineraryDays]);

    return (
        <div className={classNames(["w-full h-full  flex items-center justify-center", version === "web" ? "pt-0" : "pt-16"])}>
            <svg ref={svgRef} className="w-full h-full"></svg>
        </div>
    );
};
